import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { useRef, useState } from 'react';

import { Input } from '@/components/ui/Input';
import { XStack } from '@/components/ui/layout/layout';
import TextAreaPrimitive from '@/components/ui/TextArea';
import { cn } from '@/lib/utils';

import { emptyPlaceholder, InlineField, StackedField } from '../field';
import {
  FIELD_ERROR_UNDERLINE_CLASS,
  fieldErrorClass,
  textAppearanceClass,
} from '../field/textAppearance';
import {
  fieldErrorId,
  labelIdOf,
  useElementId,
  useFieldChecks,
} from '../fieldIds';
import {
  HELD_CLASS,
  ICON_CLASS,
  LINE_CLAMP,
  isTone,
  sizingClass,
  TEXT_BOX_CLASS,
  TEXT_STYLE,
  TONE_CLASS,
} from '../tokens';

import { ICON_GLYPH } from './Icon';
import './TextInput.css';

import type { catalog } from '@littlebird/action-cards-view';

const INPUT_TYPE = {
  default: 'text',
  email: 'email',
  url: 'url',
  numeric: 'text',
} as const;

export const TextInput: ComponentFn<typeof catalog, 'TextInput'> = ({
  props,
  bindings,
}) => {
  const id = useElementId(props);
  const inputRef = useRef<HTMLInputElement>(null);
  const [restHeight, setRestHeight] = useState(0);
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useBoundProp<string>(props.value, bindings?.value);
  const checks = useFieldChecks({
    path: bindings?.value,
    checks: props.checks,
    validateOn: props.validateOn,
    serverError: props.error,
    value,
  });
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const path = bindings?.value;
  // An add line opens the field by focusing its path before the input exists
  const open = focused || (path !== undefined && session.focused === path);
  const held = status === 'running';
  // A literal value has no state path to write to, so it shows as text
  const editable =
    path !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    !held;
  const error = checks.error ?? props.error;
  const appearance = props.appearance ?? 'inline';
  const appearanceClass = textAppearanceClass(appearance, props.outline);
  const textStyle = TEXT_STYLE[props.textStyle ?? 'body'];
  // Not a catalog prop: a component composing TextInput passes the tone its own
  // catalog props ask for
  const toneClass = isTone(props.tone) ? TONE_CLASS[props.tone] : undefined;
  // Not a catalog prop: a component composing TextInput without a visible
  // label passes the field's name
  const ariaLabel =
    !props.label && typeof props.ariaLabel === 'string'
      ? props.ariaLabel
      : undefined;

  const title =
    props.lines !== undefined ||
    props.textStyle === 'title' ||
    props.textStyle === 'value' ||
    props.textStyle === 'headline';
  const titleClamp =
    props.lines === 0
      ? undefined
      : LINE_CLAMP[Math.min(props.lines ?? 2, 10) - 1];
  const restingTitle = editable && appearance === 'inline' && title && !focused;
  const placeholder = emptyPlaceholder(
    props.placeholder,
    props.label,
    title ? 'Add title' : 'Add text'
  );
  // A title's problem underlines it in place; the message is its tooltip and
  // description
  const titleError = title && error ? FIELD_ERROR_UNDERLINE_CLASS : undefined;
  const describedBy = error ? fieldErrorId(id) : undefined;
  const Field = props.labelPosition === 'above' ? StackedField : InlineField;
  const Glyph = props.icon ? ICON_GLYPH[props.icon] : null;
  if (props.hideWhenEmpty && !value?.trim() && !open) return null;

  const field = (
    <div
      className={cn(
        'card-text-input',
        // In a row, a hugging field sizes to its text or placeholder (TextInput.css)
        props.sizing !== 'fill' && 'card-text-input-hug'
      )}
    >
      <Field
        id={id}
        label={props.label}
        labelsControl={editable}
        error={error}
        labelHidden={props.labelHidden}
      >
        <XStack className="min-w-0 items-start gap-[calc(6*var(--ac-u))]">
          {Glyph ? (
            <Glyph
              aria-hidden="true"
              className={cn(
                'mt-[calc(4*var(--ac-u))] shrink-0 text-foreground-muted',
                ICON_CLASS
              )}
            />
          ) : null}
          {restingTitle ? (
            <button
              type="button"
              id={id}
              // The Feed's E finds a title at rest by this, since focusing it opens the field
              data-field-at-rest=""
              aria-invalid={error ? true : undefined}
              aria-describedby={describedBy}
              aria-label={ariaLabel ?? props.label ?? 'Edit title'}
              title={error ?? value}
              className={cn(
                'min-h-[1lh] w-full cursor-text py-0 text-left break-words',
                appearanceClass,
                textStyle,
                toneClass,
                typeof props.weightClass === 'string' && props.weightClass,
                titleClamp,
                titleError
              )}
              onFocus={(event) => {
                setRestHeight(
                  event.currentTarget.getBoundingClientRect().height
                );
                setFocused(true);
                if (path) controls.focus(path);
              }}
              onClick={() => {
                setFocused(true);
              }}
            >
              {value || placeholder}
            </button>
          ) : editable && title ? (
            <TextAreaPrimitive
              id={id}
              autoFocus
              rows={1}
              value={value ?? ''}
              placeholder={placeholder}
              aria-label={ariaLabel}
              error={error}
              aria-describedby={describedBy}
              title={error}
              className={cn(
                TEXT_BOX_CLASS,
                appearanceClass,
                textStyle,
                toneClass,
                typeof props.weightClass === 'string' && props.weightClass,
                'min-h-[1lh] max-h-[calc(320*var(--ac-u))] resize-none overflow-y-auto py-0 [field-sizing:content] transition-[height] duration-150 motion-reduce:transition-none',
                // Leave room for the font's last pixel without shifting the next row
                'pb-px -mb-px',
                titleError
              )}
              style={{
                minHeight: restHeight
                  ? `min(${restHeight + 1}px, 20rem)`
                  : undefined,
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
              onChange={(event) => {
                setRestHeight(0);
                setValue(event.target.value.replace(/\n/g, ' '));
                checks.afterChange();
              }}
              onFocus={() => {
                if (path) controls.focus(path);
              }}
              onBlur={() => {
                setFocused(false);
                checks.onBlur();
                if (path) controls.blur(path);
              }}
            />
          ) : editable ? (
            <Input
              id={id}
              ref={inputRef}
              autoFocus={open}
              variant="plain"
              type={INPUT_TYPE[props.keyboardType ?? 'default']}
              inputMode={
                props.keyboardType === 'numeric' ? 'decimal' : undefined
              }
              autoCapitalize={props.autoCapitalize}
              autoCorrect={props.autoCorrect === false ? 'off' : undefined}
              spellCheck={props.autoCorrect}
              autoComplete="off"
              value={value ?? ''}
              placeholder={placeholder}
              aria-label={ariaLabel}
              aria-invalid={error ? true : undefined}
              aria-describedby={describedBy}
              className={cn(
                TEXT_BOX_CLASS,
                appearanceClass,
                textStyle,
                toneClass,
                typeof props.weightClass === 'string' && props.weightClass,
                'h-[1lh] min-h-[1lh] py-0 text-ellipsis',
                error && fieldErrorClass(appearance, props.outline)
              )}
              onChange={(event) => {
                setValue(event.target.value);
                checks.afterChange();
              }}
              onFocus={() => {
                setFocused(true);
                if (path) controls.focus(path);
              }}
              onBlur={() => {
                setFocused(false);
                checks.onBlur();
                if (path) controls.blur(path);
              }}
            />
          ) : (
            <span
              id={id}
              title={typeof props.lines === 'number' ? value : undefined}
              aria-labelledby={props.label ? labelIdOf(id) : undefined}
              className={cn(
                TEXT_BOX_CLASS,
                textStyle,
                toneClass,
                typeof props.weightClass === 'string' && props.weightClass,
                title && titleClamp,
                'min-h-[1lh] py-0',
                held && HELD_CLASS
              )}
            >
              {value || (
                <span className="text-foreground-muted">
                  {props.placeholder}
                </span>
              )}
            </span>
          )}
        </XStack>
      </Field>
    </div>
  );

  return props.sizing === 'fill' ? (
    <div className={sizingClass(props.sizing)}>{field}</div>
  ) : (
    field
  );
};
